import { api } from '../../lib/api';
import { useHeldResource } from '../../lib/hooks';

/**
 * The review's Active IT Projects slide (slide 13): the client's open Autotask
 * projects — status, how far along, when due, and who leads it.
 * Data: GET /am/clients/:id/autotask/projects (routes/amAutotask.ts).
 */

export interface ActiveProject {
  id: number;
  name: string;
  number: string | null;
  status: string;
  percentComplete: number | null;
  start: string | null;
  end: string | null;
  lead: string | null;
  description: string | null;
}
/** What the slide keeps on the review. */
export interface ProjectsSnap {
  linked: boolean;
  companyName: string | null;
  projects: ActiveProject[];
}

export async function loadActiveProjects(clientId: string, refresh: boolean): Promise<ProjectsSnap> {
  return api.get<ProjectsSnap>(`/am/clients/${clientId}/autotask/projects${refresh ? '?refresh=1' : ''}`);
}

const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : null;

/**
 * One card per project: name, status, a progress bar, the dates and the lead.
 * More projects, smaller cards, so the list still fits the one page.
 */
export function ProjectsList({ projects }: { projects: ActiveProject[] }) {
  const today = new Date().toISOString().slice(0, 10);
  const size = projects.length > 8 ? 'is-dense' : projects.length > 4 ? 'is-compact' : '';
  return (
    <ul className={`am-prj-list ${size}`}>
      {projects.map((p) => {
        const late = p.end !== null && p.end < today;
        const pct = Math.max(0, Math.min(100, p.percentComplete ?? 0));
        return (
          <li key={p.id} className="am-prj">
            <div className="am-prj-head">
              <span className="am-prj-name">{p.name || 'Project'}</span>
              <span className="am-prj-status">{p.status}</span>
            </div>
            <div className="am-prj-bar" aria-label={`${pct}% complete`}>
              <span style={{ width: `${pct}%` }} />
            </div>
            <div className="am-prj-meta">
              <span>{p.percentComplete === null ? 'Progress not set' : `${pct}% complete`}</span>
              {p.end && <span className={late ? 'is-late' : undefined}>{late ? 'Was due' : 'Due'} {day(p.end)}</span>}
              {p.lead && <span>Lead: {p.lead}</span>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** The review form's preview of slide 13, read from Autotask now. */
export function ProjectsPreview({ clientId }: { clientId: string }) {
  const data = useHeldResource<ProjectsSnap>(`/am/clients/${clientId}/autotask/projects`);
  const d = data.data;
  if (data.loading && !d) return <p className="small muted">Reading this client's projects from Autotask…</p>;
  if (data.error) return <p className="small muted">Could not read the client's projects from Autotask: {data.error}</p>;
  if (!d) return null;
  if (!d.linked) return <p className="small muted">This client is not linked to an Autotask company, so the slide has no projects to show.</p>;
  return (
    <div className="am-csat-preview">
      <p className="xs muted">
        Filled automatically from Autotask for {d.companyName}: {d.projects.length} active project{d.projects.length === 1 ? '' : 's'}. The
        slide keeps its own copy from the day it is presented.
      </p>
      {d.projects.length === 0 ? <p className="small muted">No active projects.</p> : <ProjectsList projects={d.projects} />}
    </div>
  );
}
